
import { useState, FormEvent } from 'react';

export default function ResidentialCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    renovationType: 'full-home',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('renovationType', formData.renovationType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6bl9oiohb161tfd7ks0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', renovationType: 'full-home', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:py-24 lg:py-28 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="space-y-6 md:space-y-8">
            <p className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">
              Start Your Renovation
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2A2A2A] leading-tight">
              Ready to Transform<br />
              Your <span className="font-serif text-[#D4AF37]">Home</span>?
            </h2>
            <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
              Book a complimentary site inspection at your Johannesburg property. Our team will assess your
              space, discuss your vision, and provide a transparent, itemised quotation.
            </p>
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-base md:text-lg text-[#D4AF37]"></i>
                </div>
                <span className="text-sm text-gray-700">Free site inspection &amp; consultation</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-base md:text-lg text-[#D4AF37]"></i>
                </div>
                <span className="text-sm text-gray-700">Transparent, itemised quotation</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-base md:text-lg text-[#D4AF37]"></i>
                </div>
                <span className="text-sm text-gray-700">Licensed contractors with full insurance</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-base md:text-lg text-[#D4AF37]"></i>
                </div>
                <span className="text-sm text-gray-700">Workmanship warranty on all projects</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
            <form
              id="residential-inquiry-form"
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-4 md:space-y-5"
            >
              <div>
                <label htmlFor="res-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="res-name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="res-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="res-email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="res-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="res-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="+27 XX XXX XXXX"
                />
              </div>

              <div>
                <label htmlFor="res-type" className="block text-sm font-semibold text-gray-700 mb-2">
                  Renovation Type
                </label>
                <select
                  id="res-type"
                  name="renovationType"
                  value={formData.renovationType}
                  onChange={(e) => setFormData({ ...formData, renovationType: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="full-home">Full Home Renovation</option>
                  <option value="kitchen">Kitchen Remodelling</option>
                  <option value="bathroom">Bathroom Renovation</option>
                  <option value="structural">Structural Alteration</option>
                  <option value="outdoor">Outdoor Living Space</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="res-message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Details
                </label>
                <textarea
                  id="res-message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your renovation vision..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-3.5 md:py-4 bg-[#D4AF37] text-white text-sm md:text-base font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Request Free Site Inspection'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Thank you! We&apos;ll contact you shortly to schedule your site inspection.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    Something went wrong. Please try again or call us at 011 875 2075.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
